import { api } from '@orisini/ui/lib/api'
import type { DataTemplate } from '@orisini/studio-kit/templates/data-template'

/** Klien Brand & Styles + Content Formats (orisini-studio lewat /api/studio/*). */

export interface BrandFile {
  path: string
  title: string | null
  description: string | null
  content: string
  version: number
  source: 'starter' | 'ai' | 'manual'
  updatedAt: number
}

export interface Style {
  kind: 'carousel' | 'video'
  slug: string
  name: string
  active: boolean
  path: string
}

export interface TemplateInfo {
  kind: 'poster' | 'carousel' | 'video'
  style: string | null
  source: 'compiled' | 'default'
  version: number | null
  upToDate: boolean
  compiledAt: number | null
  lastError: { at: number; message: string | null } | null
  def: DataTemplate
}

export interface BrandState {
  files: BrandFile[]
  styles: Style[]
  templates: TemplateInfo[]
  maxLength: number
}

export type StyleMode = 'random' | 'sequential' | 'fixed'
export type DurationMode = 'auto' | '1-5' | '5-10' | 'custom'
export interface StylePick {
  enabled: boolean
  mode: StyleMode
  styles: string[]
  fixedStyle: string | null
}
export interface Formats {
  poster: { enabled: boolean }
  carousel: StylePick
  video: StylePick & { duration: { mode: DurationMode; minutes: number | null } }
}

const base = (id: string) => `/studio/workspaces/${id}`

export const brandApi = {
  load: (id: string) => api.get<BrandState>(`${base(id)}/brand`),
  save: (id: string, path: string, content: string, instruction?: string) =>
    api.put<{ file: BrandFile }>(`${base(id)}/brand/file`, { path, content, instruction }).then((r) => r.file),
  propose: (id: string, path: string, instruction: string) =>
    api.post<{ content: string; summary: string | null }>(`${base(id)}/brand/propose`, { path, instruction }),
  compile: (id: string, kind: 'poster' | 'carousel' | 'video', style?: string) =>
    api.post<{ ok: boolean; errors?: string[]; template: TemplateInfo }>(`${base(id)}/templates/compile`, { kind, style }),
  createStyle: (id: string, kind: 'carousel' | 'video', name: string, description: string) =>
    api.post<{ style: Style; aiWrote: boolean }>(`${base(id)}/styles`, { kind, name, description }),
  updateStyle: (id: string, kind: string, slug: string, patch: { name?: string; active?: boolean }) =>
    api.patch(`${base(id)}/styles/${kind}/${slug}`, patch),
  deleteStyle: (id: string, kind: string, slug: string) => api.delete(`${base(id)}/styles/${kind}/${slug}`),

  formats: (id: string) => api.get<{ formats: Formats; styles: Omit<Style, 'path'>[] }>(`${base(id)}/formats`),
  saveFormats: (id: string, formats: Formats) =>
    api.put<{ formats: Formats; styles: Omit<Style, 'path'>[] }>(`${base(id)}/formats`, formats),
}

export type DiffLine = { op: 'same' | 'add' | 'del'; text: string }

/**
 * Diff per baris (LCS). Berkas MD dibatasi 8000 karakter, jadi beberapa ratus
 * baris paling banyak — tabel O(n·m) masih ringan.
 */
export function diffLines(a: string, b: string): DiffLine[] {
  const x = a.replace(/\n$/, '').split('\n')
  const y = b.replace(/\n$/, '').split('\n')
  const n = x.length
  const m = y.length
  const t: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) t[i]![j] = x[i] === y[j] ? t[i + 1]![j + 1]! + 1 : Math.max(t[i + 1]![j]!, t[i]![j + 1]!)
  }
  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (x[i] === y[j]) {
      out.push({ op: 'same', text: x[i]! })
      i++
      j++
    } else if (t[i + 1]![j]! >= t[i]![j + 1]!) out.push({ op: 'del', text: x[i++]! })
    else out.push({ op: 'add', text: y[j++]! })
  }
  while (i < n) out.push({ op: 'del', text: x[i++]! })
  while (j < m) out.push({ op: 'add', text: y[j++]! })
  return out
}
